import React, { ReactNode, useEffect, useState, type JSX } from 'react';
import FlexboxContainerComponent from 'components/UI/FlexboxContainer';
import Modal from 'components/UI/Modal';
import Select from 'components/UI/Select';
import { Link } from '@mui/icons-material';
import styled from 'styled-components';
import { styled as muiStyled } from '@mui/material/styles';
import Button from 'components/UI/Button';
import Vendor from 'data/Vendor';
import { Space } from 'styles/themeSpaces';
import Warehouse from 'data/Warehouse';
import InputComponent from 'components/UI/Input';
import { Autocomplete } from '@mui/material';
import { Border, Text } from 'styles/themeColors';
import { TextField, TextFieldProps } from '@mui/material';
import { BusinessUnit } from 'data/BusinessUnit';
import FeatureFlag from 'data/FeatureFlag';
import { LABELS_TEXT } from 'constants/pricebooks';

const Label = styled.span<any>`
  font-size: 16px;
  line-height: 20px;
  color: ${({ $color }) => $color};
`;
const Span = styled.span`
  margin: ${Space.spBase} 0;
`;

const AutocompleteStyled = muiStyled(Autocomplete)({
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: Border.main,
  },
  '& .MuiSvgIcon-root': {
    color: Text.primary,
  },
}) as any;

// Listbox styles for Autocomplete
const listboxSx = {
  height: 250,
  overflowX: 'hidden'
};

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm?: (value: string) => void;
  isLoading?: boolean;
  userType?: string;
  assigneeList: Vendor[];
  warehouses: Warehouse[];
  setAssignee: (assignee: Option) => void;
  setWarehouse: (warehouse: Option | undefined) => void;
  assignee?: Option;
  selectedVendor?: Vendor;
  assignedByError: boolean;
  warehouseError: boolean;
  warehouse?: Option;
  businessUnitError?: boolean;
  selectedBusinessUnit?: BusinessUnit;
  selectedBusinessUnitOption?: Option;
  setSelectedBusinessUnit?: (businessUnit: Option) => void;
  featureFlags?: FeatureFlag;
  businessUnitList?: BusinessUnit[];
}

interface Option {
  value: string;
  id?: string;
  icon?: ReactNode;
  badge?: any;
}

const NewGeographyPricebooksModal = ({
  show,
  onClose,
  onConfirm,
  assigneeList,
  warehouses,
  setWarehouse,
  setAssignee,
  assignee,
  selectedVendor,
  assignedByError,
  warehouseError,
  warehouse,
  businessUnitError,
  selectedBusinessUnit,
  selectedBusinessUnitOption,
  setSelectedBusinessUnit,
  featureFlags,
  businessUnitList
}: Props) => {

  const [warehouseList, setWarehouseList] = useState<Option[]>([]);
  const [assignedByList, setAssignedByList] = useState<Option[]>([]);
  const [mappedBusinessUnitList, setMappedBusinessUnitList] = useState<Option[]>([]);
  const [cancelModal, setCancelModal] = useState(false);

  const linkIconStyle = { height: '25', width: '30', color: Text.primary, margin: '25px 0px 0px 0px' };

  const selectedWarehouseOption = typeof warehouse !== 'string' ? {
    value: warehouse?.value || '',
    id: warehouse?.value
  } as Option : undefined;

  useEffect(() => {
    const options = warehouses.map((warehouse: any) => ({
      value: warehouse.name,
      id: warehouse.id,
      description: warehouse.address?.country?.countryName || ''
    }));

    setWarehouseList([...options]);
  }, [warehouses]);

  useEffect(() => {
    const options = assigneeList.map((a: any) => ({
      value: a.name,
      id: a.code,
    }));
    setAssignedByList([...options]);
  }, [assigneeList]);

  useEffect(() => {
    const options = businessUnitList?.map((businessUnit: any) => ({
      value: businessUnit.name,
      id: businessUnit.code,
    }));
    setMappedBusinessUnitList(options ? [...options] : []);
  }, [businessUnitList]);

  const onSelectAssignee = (event: any) => {
    setAssignee(event.target);
  };

  const onSelectWarehouse = (event: any, newValue: Option) => {
    setWarehouse(newValue);
  }

  const onBlur = (event: any) => {
    if (!event.target.value.length) {
      setWarehouse && setWarehouse(undefined);
    }
  };

  const onSelectBusinessUnit = (event: any) => {
    if (setSelectedBusinessUnit) {
      setSelectedBusinessUnit(event.target);
    }
  };

  return (
    <>
      <Modal
        width="794px"
        showModal={cancelModal ? false : show}
        onClose={onClose}
        showButtons={false}
        title="New geography pricebook"
        modalFooterMargin="0"
        left="50vw"
        marginLeft="-397px"
        iconName="PinDrop"
        showCloseIcon
      >
        <FlexboxContainerComponent flexDirection="column">
          <FlexboxContainerComponent
            flexDirection="row"
            width="100%"
            padding="90px 0px 0px 0px"
            alignItems='center'
          >
            {
              (!selectedVendor || (featureFlags?.businessUnit?.enabled && !selectedBusinessUnit)) &&
              <>
                <FlexboxContainerComponent flexDirection="column" width='100%'>
                  {!selectedVendor && (
                    <FlexboxContainerComponent flexDirection="column" width="100%">
                      <FlexboxContainerComponent padding="0px 0px 8px 0">
                        {' '}
                        <Label $hasError={assignedByError}>{LABELS_TEXT.ASSIGN_BY}</Label>
                      </FlexboxContainerComponent>
                      <FlexboxContainerComponent
                        flexDirection="row"
                        alignItems="center"
                        width="100%"
                      >
                        <Select
                          options={assignedByList || []}
                          onChange={onSelectAssignee}
                          value={assignee?.value || ''}
                          defaultValue="Select"
                          width={"100%"}
                          height="36px"
                          isInvalid={assignedByError}
                        />
                      </FlexboxContainerComponent>
                    </FlexboxContainerComponent>
                  )}
                  {
                    featureFlags?.businessUnit?.enabled && !selectedBusinessUnit && (
                      <FlexboxContainerComponent flexDirection="column" width="100%" margin='5px 0px'>
                        <FlexboxContainerComponent padding="0px 0px 8px 0">
                          {' '}
                          <Label $hasError={businessUnitError}>{LABELS_TEXT.BUSINESS_UNIT}</Label>
                        </FlexboxContainerComponent>
                        <FlexboxContainerComponent
                          flexDirection="row"
                          alignItems="center"
                          width="100%"
                        >
                          <Select
                            options={mappedBusinessUnitList || []}
                            onChange={onSelectBusinessUnit}
                            value={selectedBusinessUnitOption?.value || ''}
                            defaultValue="Select"
                            width={"100%"}
                            isInvalid={businessUnitError}
                          />
                        </FlexboxContainerComponent>
                      </FlexboxContainerComponent>
                    )
                  }
                </FlexboxContainerComponent>
                <Link style={linkIconStyle} />
              </>
            }
            <FlexboxContainerComponent flexDirection="row" width="100%">
              {!warehouseList ?
                <InputComponent
                  id='assigned-to'
                  labelName={'Assigned to warehouse'}
                  labelPosition='top'
                  value={warehouse?.value as string}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  }}
                  errorMessage=''
                  name='assigned-to'
                  blocked={true}
                ></InputComponent> :
                <FlexboxContainerComponent flexDirection="column" width="100%">
                  <FlexboxContainerComponent padding="0px 0px 8px 0">
                    {' '}
                    <Label $color={warehouseError ? "#C82135" : ""}>Assigned to warehouse</Label>
                  </FlexboxContainerComponent>
                  <FlexboxContainerComponent
                    flexDirection="row"
                    alignItems="center"
                    width="100%"
                  >

                    <AutocompleteStyled
                      slotProps={{ listbox: { sx: listboxSx } }}
                      id="assigned-to"
                      options={warehouseList}
                      getOptionLabel={(option: any) => option.value}
                      style={{
                        width: `100%`
                      }}
                      value={selectedWarehouseOption}
                      disableClearable={true}
                      onChange={onSelectWarehouse}
                      onBlur={onBlur}
                      renderInput={AutoCompleteInput}
                      renderOption={WarehouseOptionItem}
                    />
                  </FlexboxContainerComponent>
                </FlexboxContainerComponent>
              }
            </FlexboxContainerComponent>
          </FlexboxContainerComponent>

          <FlexboxContainerComponent
            padding="104px 0 0 0"
            justifyContent="flex-end"
          >
            <Button id="modal-main-button" bgColor="#FC780B" onClick={onConfirm}>
              Next
            </Button>
          </FlexboxContainerComponent>
        </FlexboxContainerComponent>
      </Modal>
    </>);
};

export default NewGeographyPricebooksModal;

const AutoCompleteInput = (params: JSX.IntrinsicAttributes & TextFieldProps) => {
  return <TextField
    {...params}
    variant='outlined'
    inputProps={{
      ...params.inputProps,
      style: {
        padding: 0,
        textOverflow: 'ellipsis'
      },
      placeholder: 'Start typing warehouse name',
    }}
  />
};

const WarehouseOptionItem = (props: React.HTMLAttributes<HTMLLIElement>, option: any) => (
  <li {...props}>
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%'
    }}>
      <div
        style={
          { fontSize: '14px', fontWeight: 'bold' }
        }>
        {option.value}
      </div>
      <div style={{ fontSize: '12px', fontStyle: 'italic', color: Text.placeholder }}>
        {option.description}
      </div>
    </div>
  </li>
)